import React, { useState } from "react";
import { useBilling } from "./Contexts/BillingContext";
import { useAuth } from "./Contexts/AuthContext";
import { isAdminLike } from "../services/utils/utiltyFunctions";
import { apiClient } from "../services/api/apiClient";
import { showCustomToast } from "./CustomToast";

// Persistent bar under the nav on every page. Cannot be dismissed. Every
// user in the org sees it; only admins get the payment button. Wording per
// docs/billing-plan.md section 2.7.

function listNames(names: string[]) {
  if (names.length > 5) return `${names.length} systems`;
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export async function openBillingPage(kind: "checkout-session" | "portal-session") {
  const res = await apiClient.post(`/api/v1/billing/${kind}`);
  if (!res) return;
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.url) {
    showCustomToast("Billing", body.message || "Could not open billing", "error");
    return;
  }
  window.location.assign(body.url);
}

const BillingBanner: React.FC = () => {
  const { billing } = useBilling();
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  if (!billing || !billing.configured) return null;

  const admin = isAdminLike(auth.user?.role);
  const names = billing.systemNames || [];
  const several = names.length !== 1;

  let text: string | null = null;
  let tone: "red" | "yellow" = "red";
  let action: { label: string; kind: "checkout-session" | "portal-session" } | null = null;

  if (!billing.entitled && billing.enforcement === "enforce") {
    switch (billing.reason) {
      case "past_due":
      case "unpaid":
        text = names.length
          ? several
            ? `Licenses expired for ${listNames(names)}: payment failed.`
            : `${names[0]} license expired: payment failed.`
          : "Payment failed. Your subscription is inactive.";
        action = { label: "Update payment", kind: "portal-session" };
        break;
      case "suspended":
        text = "This organization's access has been suspended. Contact EpicareAlert support.";
        break;
      case "canceled":
      case "no_subscription":
        text = names.length
          ? `Licenses expired for ${listNames(names)}: no active subscription.`
          : "No active subscription. Systems cannot connect until you subscribe.";
        action = { label: "Subscribe", kind: "checkout-session" };
        break;
      default:
        text = "Subscription inactive. Systems cannot connect.";
        action = { label: "Manage billing", kind: "portal-session" };
    }
  } else if (billing.entitled && admin) {
    const daysLeft = (iso: string | null) =>
      iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000) : null;
    const trialDays = billing.reason === "trial" || billing.reason === "trialing" ? daysLeft(billing.trialEndsAt) : null;
    if (trialDays !== null && trialDays <= 7) {
      tone = "yellow";
      text = `Your free trial ends in ${Math.max(0, trialDays)} day${trialDays === 1 ? "" : "s"}. Add a payment method to keep your systems connected.`;
      action = { label: billing.hasSubscription ? "Add payment method" : "Subscribe", kind: billing.hasSubscription ? "portal-session" : "checkout-session" };
    } else if (billing.cancelAtPeriodEnd) {
      tone = "yellow";
      text = `Your subscription is set to cancel on ${new Date(billing.currentPeriodEnd || "").toLocaleDateString("en-US")}. Systems will stop connecting after that date.`;
      action = { label: "Manage billing", kind: "portal-session" };
    }
  }

  if (!text) return null;

  const colors =
    tone === "red"
      ? { bg: "#dc2626", fg: "white", btnBg: "white", btnFg: "#dc2626" }
      : { bg: "#f59e0b", fg: "#1f2937", btnBg: "#1f2937", btnFg: "white" };

  return (
    <div
      role="alert"
      style={{
        background: colors.bg,
        color: colors.fg,
        padding: "10px 20px",
        display: "flex",
        alignItems: "center",
        gap: "16px",
        flexWrap: "wrap",
        fontSize: "14px",
        fontWeight: 600,
        position: "sticky",
        top: 0,
        zIndex: 1050,
      }}
    >
      <span style={{ flex: 1 }}>{text}</span>
      {admin && action && (
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await openBillingPage(action!.kind);
            } finally {
              setBusy(false);
            }
          }}
          style={{
            background: colors.btnBg,
            color: colors.btnFg,
            border: "none",
            borderRadius: "8px",
            padding: "6px 14px",
            fontWeight: 700,
            fontSize: "13px",
            cursor: "pointer",
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

export default BillingBanner;
